import React from 'react';
import { BuildingStorefrontIcon, TruckIcon, UserCircleIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { JourneyStep } from './journeySteps';

type JourneyRole = 'consumer' | 'distributor' | 'logistics' | 'collector' | 'producer';

const roleByStep: Record<JourneyStep['id'], JourneyRole> = {
  subscribe: 'consumer',
  allotment: 'distributor',
  collection: 'distributor',
  'farm-logistics': 'logistics',
  milking: 'producer',
  filling: 'producer',
  locking: 'producer',
  traceability: 'collector',
  'cold-chain': 'logistics',
  distributor: 'distributor',
  delivery: 'distributor',
  unlock: 'consumer',
  enjoy: 'consumer',
  proof: 'consumer',
};

const roleLabels: Record<'en' | 'hi', Record<JourneyRole, string>> = {
  en: { consumer: 'Consumer', distributor: 'Distributor', logistics: 'Logistics', collector: 'Collector', producer: 'Producer' },
  hi: { consumer: 'उपभोक्ता', distributor: 'वितरक', logistics: 'परिवहन', collector: 'संग्रहकर्ता', producer: 'उत्पादक' },
};

const roleIcons = {
  consumer: UserCircleIcon,
  distributor: BuildingStorefrontIcon,
  logistics: TruckIcon,
  collector: UserGroupIcon,
};

type JourneyProgressProps = {
  currentStep: number;
  steps: JourneyStep[];
  language: 'en' | 'hi';
  onSelect: (step: number) => void;
};

const JourneyProgress: React.FC<JourneyProgressProps> = ({ currentStep, steps, language, onSelect }) => (
  <div className="border-t border-[rgba(27,94,32,0.12)] bg-white/80 px-4 py-4 backdrop-blur-sm">
    <div className="mx-auto flex max-w-6xl items-start gap-3">
      <p className="shrink-0 text-xs font-bold uppercase tracking-[0.16em] text-[var(--color-forest)]">{language === 'hi' ? `चरण ${currentStep + 1} / ${steps.length}` : `Step ${currentStep + 1} of ${steps.length}`}</p>
      <div className="flex min-w-0 flex-1 gap-1.5 pb-7" aria-label={language === 'hi' ? 'दूध यात्रा की प्रगति' : 'Journey progress'}>
        {steps.map((step, index) => {
          const role = roleByStep[step.id];
          const RoleIcon = role === 'producer' ? null : roleIcons[role];
          return (
          <div key={step.id} className="relative min-w-0 flex-1">
            <button type="button" onClick={() => onSelect(index)} aria-label={language === 'hi' ? `${step.title} पर जाएं` : `Go to ${step.title}`} aria-current={index === currentStep ? 'step' : undefined} className={`block h-2.5 w-full min-w-2 rounded-full transition motion-reduce:transition-none ${index === currentStep ? 'bg-[var(--color-forest)]' : index < currentStep ? 'bg-[var(--color-leaf)]' : 'bg-[rgba(27,94,32,0.15)]'}`} />
            {index === currentStep && (
              <span className={`absolute top-3 whitespace-nowrap text-xs font-bold text-[var(--color-forest)] ${index === 0 ? 'left-0' : index === steps.length - 1 ? 'right-0' : 'left-1/2 -translate-x-1/2'}`}>
                <span className="mx-auto block h-2 w-px bg-[var(--color-forest)]" />
                <span className="flex items-center gap-1">{RoleIcon ? <RoleIcon className="h-4 w-4" /> : <img src="/rightoz-bottle-007.svg" alt="" className="h-5 w-3 object-contain" />}{roleLabels[language][role]}</span>
              </span>
            )}
          </div>
          );
        })}
      </div>
    </div>
  </div>
);

export default JourneyProgress;
